import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { workerScheduleService } from '../../services/workerScheduleService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';

// ─── Helpers ────────────────────────────────────────────────────────────────

const getDays = () => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d?.setDate(d?.getDate() + i);
    const dateStr = d?.toISOString()?.split('T')?.[0];
    const label =
      i === 0
        ? 'Today'
        : i === 1
        ? 'Tomorrow' : d?.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    const shortDate = d?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    days?.push({ dateStr, label, shortDate, isToday: i === 0 });
  }
  return days;
};

const SITE_COLORS = [
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-emerald-100 text-emerald-800 border-emerald-200',
  'bg-violet-100 text-violet-800 border-violet-200',
  'bg-amber-100 text-amber-800 border-amber-200',
  'bg-rose-100 text-rose-800 border-rose-200',
  'bg-cyan-100 text-cyan-800 border-cyan-200',
  'bg-orange-100 text-orange-800 border-orange-200',
  'bg-teal-100 text-teal-800 border-teal-200',
];

const getSiteColor = (siteId, sites) => {
  const idx = sites?.findIndex(s => s?.id === siteId);
  return SITE_COLORS?.[idx % SITE_COLORS?.length] || SITE_COLORS?.[0];
};

// ─── SiteChip (draggable) ────────────────────────────────────────────────────

const SiteChip = ({ site, colorClass, draggable = true, compact = false, onDragStart = undefined }) => {
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      className={`
        inline-flex items-center gap-1.5 rounded-md border font-medium cursor-grab active:cursor-grabbing select-none
        ${compact ? 'px-2 py-0.5 text-xs' : 'px-3 py-1.5 text-sm'}
        ${colorClass}
        ${draggable ? 'hover:opacity-80 transition-opacity shadow-sm' : ''}
      `}
    >
      <Icon name="MapPin" size={compact ? 10 : 12} />
      <span className="truncate max-w-[120px]">{site?.site_name}</span>
    </div>
  );
};

// ─── AssignmentCell ──────────────────────────────────────────────────────────

const AssignmentCell = ({
  worker,
  day,
  assignedSiteId,
  sites,
  saving,
  onDrop,
  onRemove,
  onTapAssign,
  isMobile,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const assignedSite = sites?.find(s => s?.id === assignedSiteId);
  const colorClass = assignedSiteId ? getSiteColor(assignedSiteId, sites) : '';

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    const siteId = e?.dataTransfer?.getData('siteId');
    if (siteId) onDrop(worker?.id, day?.dateStr, siteId);
  };

  return (
    <td
      onDragOver={!isMobile ? handleDragOver : undefined}
      onDragLeave={!isMobile ? handleDragLeave : undefined}
      onDrop={!isMobile ? handleDrop : undefined}
      className={`
        px-2 py-2 transition-colors relative
        ${isDragOver ? 'bg-accent/15 ring-2 ring-inset ring-accent/40' : ''}
        ${day?.isToday ? 'bg-accent/3' : ''}
      `}
      style={{ minWidth: 140 }}
    >
      {saving ? (
        <div className="flex items-center justify-center h-9">
          <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      ) : assignedSite ? (
        <div className="flex items-center gap-1 group">
          {/* Assigned chip — also draggable to move */}
          <div
            draggable={!isMobile}
            onDragStart={!isMobile ? (e) => {
              e?.dataTransfer?.setData('siteId', assignedSite?.id);
              e?.dataTransfer?.setData('sourceWorkerId', worker?.id);
              e?.dataTransfer?.setData('sourceDate', day?.dateStr);
            } : undefined}
            className="flex-1 min-w-0"
          >
            <SiteChip site={assignedSite} colorClass={colorClass} compact draggable={!isMobile} />
          </div>
          <button
            onClick={() => onRemove(worker?.id, day?.dateStr)}
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-0.5 rounded text-muted-foreground hover:text-destructive transition-all shrink-0"
            title="Remove assignment"
          >
            <Icon name="X" size={13} />
          </button>
        </div>
      ) : (
        <div
          className={`
            h-9 rounded-md border-2 border-dashed flex items-center justify-center text-xs text-muted-foreground
            ${isDragOver ? 'border-accent bg-accent/10 text-accent' : 'border-border/50'}
            ${isMobile ? 'cursor-pointer hover:border-accent/60 hover:bg-accent/5' : 'cursor-default'}
          `}
          onClick={isMobile ? () => onTapAssign(worker?.id, day?.dateStr) : undefined}
        >
          {isDragOver ? (
            <span className="font-medium">Drop here</span>
          ) : isMobile ? (
            <span>Tap to assign</span>
          ) : (
            <span>—</span>
          )}
        </div>
      )}
    </td>
  );
};

// ─── TapAssignModal (mobile fallback) ────────────────────────────────────────

const TapAssignModal = ({ worker, day, sites, currentSiteId, onAssign, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
          <div>
            <p className="text-sm font-semibold text-foreground">{worker?.full_name || 'Worker'}</p>
            <p className="text-xs text-muted-foreground">{day?.label} · {day?.shortDate}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
          {currentSiteId && (
            <button
              onClick={() => onAssign(null)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-destructive/30 bg-destructive/5 text-destructive text-sm font-medium hover:bg-destructive/10 transition-colors"
            >
              <Icon name="Trash2" size={15} />
              Remove assignment
            </button>
          )}
          {sites?.map((site, idx) => (
            <button
              key={site?.id}
              onClick={() => onAssign(site?.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-colors
                ${currentSiteId === site?.id
                  ? 'border-accent bg-accent/10 text-accent' :'border-border hover:bg-muted/50 text-foreground'
                }
              `}
            >
              <Icon name="MapPin" size={15} />
              <span className="flex-1 text-left truncate">{site?.site_name}</span>
              {currentSiteId === site?.id && <Icon name="Check" size={14} />}
            </button>
          ))}
          {sites?.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No active sites available</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const WorkerSchedule = () => {
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [workers, setWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [tapModal, setTapModal] = useState(null); // { workerId, dateStr }
  const [isMobile, setIsMobile] = useState(false);

  const days = getDays();

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (userProfile?.role !== 'admin') {
      navigate('/admin-dashboard', { replace: true });
      return;
    }
    loadData();
  }, [userProfile, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const companyResult = await companyService?.getUserCompany();
      if (companyResult?.error) throw companyResult.error;
      const cId = companyResult?.data?.id;
      setCompanyId(cId);

      const [workersResult, sitesResult, assignmentsResult] = await Promise.all([
        workerScheduleService?.getCompanyWorkers(cId),
        siteService?.getAllSites(),
        workerScheduleService?.getAssignmentsRange(
          cId,
          days?.[0]?.dateStr,
          days?.[days?.length - 1]?.dateStr
        ),
      ]);

      if (workersResult?.error) throw workersResult.error;
      if (sitesResult?.error) throw sitesResult.error;

      setWorkers(workersResult?.data || []);
      setSites((sitesResult?.data || [])?.filter(s => s?.status === 'active'));

      const map = {};
      (assignmentsResult?.data || [])?.forEach(a => {
        map[`${a.user_id}_${a.assignment_date}`] = a?.site_id;
      });
      setAssignments(map);
    } catch (err) {
      console.error('[WorkerSchedule] load schedule failed:', err);
      setError('Failed to load schedule data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignment = useCallback(async (userId, dateStr, siteId) => {
    if (subscriptionReadOnly) return;
    const key = `${userId}_${dateStr}`;
    setSaving(prev => ({ ...prev, [key]: true }));
    setError('');

    try {
      if (siteId) {
        const result = await workerScheduleService?.setAssignment(userId, siteId, companyId, dateStr);
        if (result?.error) throw result.error;
      } else {
        const result = await workerScheduleService?.removeAssignment(userId, dateStr);
        if (result?.error) throw result.error;
      }

      setAssignments(prev => ({ ...prev, [key]: siteId || null }));
      setSuccessMessage('Saved');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('[WorkerSchedule] update assignment failed:', err);
      setError('Failed to update assignment. Please try again.');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  }, [companyId, subscriptionReadOnly]);

  const handleDrop = useCallback((userId, dateStr, siteId) => {
    handleAssignment(userId, dateStr, siteId);
  }, [handleAssignment]);

  const handleRemove = useCallback((userId, dateStr) => {
    handleAssignment(userId, dateStr, null);
  }, [handleAssignment]);

  const handleTapAssign = useCallback((workerId, dateStr) => {
    if (subscriptionReadOnly) return;
    setTapModal({ workerId, dateStr });
  }, [subscriptionReadOnly]);

  const handleTapModalAssign = useCallback((siteId) => {
    if (!tapModal) return;
    handleAssignment(tapModal?.workerId, tapModal?.dateStr, siteId);
    setTapModal(null);
  }, [tapModal, handleAssignment]);

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  const tapWorker = tapModal ? workers?.find(w => w?.id === tapModal?.workerId) : null;
  const tapDay = tapModal ? days?.find(d => d?.dateStr === tapModal?.dateStr) : null;
  const tapCurrentSiteId = tapModal ? assignments?.[`${tapModal?.workerId}_${tapModal?.dateStr}`] || null : null;

  return (
    <>
      <Helmet>
        <title>Worker Schedule - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-full mx-auto px-4 py-8">

            <SubscriptionReadOnlyBanner className="mb-6" />

            {/* Page Header */}
            <div className="mb-6">
              <Button
                onClick={() => navigate('/admin-dashboard')}
                variant="ghost"
                className="mb-4 flex items-center gap-2"
              >
                <Icon name="ArrowLeft" size={18} />
                <span>Back to Dashboard</span>
              </Button>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Worker Schedule</h1>
                  <p className="text-muted-foreground mt-1">
                    {isMobile
                      ? 'Tap a cell to assign a worker to a site' :'Drag a site onto a worker/day cell to assign. Drag an existing chip to move it.'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {successMessage && (
                    <span className="flex items-center gap-1.5 text-sm text-success font-medium animate-pulse">
                      <Icon name="CheckCircle" size={15} />
                      {successMessage}
                    </span>
                  )}
                  <Button
                    onClick={() => navigate('/invite-users')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Icon name="UserPlus" size={16} />
                    <span>Invite Worker</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-5 flex items-start gap-3">
                <Icon name="AlertCircle" size={18} className="text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* No workers */}
            {workers?.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No team members yet</h3>
                <p className="text-muted-foreground mb-6">Invite workers or site managers to your company to start scheduling them.</p>
                <Button onClick={() => navigate('/invite-users')} className="flex items-center gap-2 mx-auto">
                  <Icon name="UserPlus" size={16} />
                  <span>Invite Team</span>
                </Button>
              </div>
            ) : (
              <div className="flex gap-5 items-start">

                {/* ── Sites Panel (desktop only) ── */}
                {!isMobile && (
                  <div className="w-52 shrink-0 bg-card border border-border rounded-xl shadow-sm sticky top-20 self-start">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Icon name="Building2" size={15} className="text-accent" />
                        Sites
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">Drag onto a cell</p>
                    </div>
                    <div className="p-3 space-y-2">
                      {sites?.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-4">No active sites</p>
                      ) : (
                        sites?.map((site) => (
                          <div
                            key={site?.id}
                            draggable
                            onDragStart={(e) => {
                              e?.dataTransfer?.setData('siteId', site?.id);
                            }}
                            className="cursor-grab active:cursor-grabbing"
                          >
                            <SiteChip
                              site={site}
                              colorClass={getSiteColor(site?.id, sites)}
                              draggable={false}
                              onDragStart={undefined}
                            />
                          </div>
                        ))
                      )}
                    </div>
                    {/* Legend */}
                    <div className="px-4 py-3 border-t border-border">
                      <p className="text-xs text-muted-foreground font-medium mb-2">How to use</p>
                      <ul className="space-y-1 text-xs text-muted-foreground">
                        <li className="flex items-start gap-1.5"><Icon name="ArrowRight" size={11} className="mt-0.5 shrink-0" />Drag site → cell to assign</li>
                        <li className="flex items-start gap-1.5"><Icon name="ArrowRight" size={11} className="mt-0.5 shrink-0" />Drag chip → new cell to move</li>
                        <li className="flex items-start gap-1.5"><Icon name="ArrowRight" size={11} className="mt-0.5 shrink-0" />Hover chip → × to remove</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* ── Schedule Grid ── */}
                <div className="flex-1 min-w-0 bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full" style={{ minWidth: isMobile ? 600 : 900 }}>
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground sticky left-0 bg-muted/50 z-10 min-w-[160px]">
                            Worker
                          </th>
                          {days?.map(day => (
                            <th
                              key={day?.dateStr}
                              className={`px-2 py-3 text-center text-xs font-semibold ${
                                day?.isToday ? 'text-accent' : 'text-foreground'
                              }`}
                              style={{ minWidth: 140 }}
                            >
                              <div className={day?.isToday ? 'font-bold' : ''}>{day?.label}</div>
                              {!day?.isToday && (
                                <div className="text-xs font-normal text-muted-foreground mt-0.5">{day?.shortDate}</div>
                              )}
                              {day?.isToday && (
                                <div className="text-xs font-normal text-accent/70 mt-0.5">{day?.shortDate}</div>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {workers?.map(worker => (
                          <tr key={worker?.id} className="hover:bg-muted/10 transition-colors">
                            {/* Worker cell */}
                            <td className="px-4 py-3 sticky left-0 bg-card z-10 border-r border-border">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                                  <Icon name="User" size={16} className="text-accent" />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-sm font-medium text-foreground truncate">
                                      {worker?.full_name || 'Unnamed'}
                                    </p>
                                    {worker?.is_active === false && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                                        Pending invite
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground truncate">{worker?.email}</p>
                                </div>
                              </div>
                            </td>

                            {/* Day cells */}
                            {days?.map(day => {
                              const key = `${worker?.id}_${day?.dateStr}`;
                              return (
                                <AssignmentCell
                                  key={day?.dateStr}
                                  worker={worker}
                                  day={day}
                                  assignedSiteId={assignments?.[key] || null}
                                  sites={sites}
                                  saving={saving?.[key] || false}
                                  onDrop={handleDrop}
                                  onRemove={handleRemove}
                                  onTapAssign={handleTapAssign}
                                  isMobile={isMobile}
                                />
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: Sites list below grid */}
                  {isMobile && sites?.length > 0 && (
                    <div className="border-t border-border px-4 py-3">
                      <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                        Available Sites — tap a cell above to assign
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {sites?.map(site => (
                          <SiteChip
                            key={site?.id}
                            site={site}
                            colorClass={getSiteColor(site?.id, sites)}
                            draggable={false}
                            compact
                            onDragStart={undefined}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      {/* Mobile tap-to-assign modal */}
      {tapModal && tapWorker && tapDay && (
        <TapAssignModal
          worker={tapWorker}
          day={tapDay}
          sites={sites}
          currentSiteId={tapCurrentSiteId}
          onAssign={handleTapModalAssign}
          onClose={() => setTapModal(null)}
        />
      )}
    </>
  );
};

export default WorkerSchedule;
